import buildingModelCommentConstants from "../constants/buildingModelCommentConstants";
import { api } from "../api";
import { getToken } from "../utils/local-storage.util";

/**
 * Retrieves all building model comments for a file
 *
 * SUCCESS:
 *    1. Populates buildingModelComments.comments with comments array
 *    2. Triggers buildingModelCommentActionSuccess
 *
 * ERROR: Triggers buildingModelCommentActionFailure()
 *
 * @param {number} fileId - unique building discipline file identifier
 * @param {function} cb - callback function
 * @returns http response with array of comments
 */
export const getBuildingModelComments = (fileId, cb = () => {}) => {
	return (dispatch) => {
		dispatch({
			type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_REQUEST,
		});

		const requestOptions = {
			method: "GET",
			url: `/building-model-comment/${fileId}`,
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getToken()}`, // Use getToken to retrieve the token
			},
		};

		return api(requestOptions)
			.then((response) => {
				dispatch({
					type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_SUCCESS,
					data: response.data.result,
				});
				cb(response.data.result);
			})
			.catch((err) => {
				dispatch(
					buildingModelCommentActionFailure(err.response?.data?.message)
				);
			});
	};
};

/**
 * CREATE building model comment
 *
 * SUCCESS:
 * 		1. Triggers buildingModelCommentCreateSuccess
 * 		2. setBuildingModelCommentSuccessMessage
 * 		3. Optionally refresh comments list
 *
 * @param {number} fileId - unique building discipline file identifier
 * @param {object} commentData - Contains comment text, metadata, cameraData, etc.
 * @param {function} cb - callback function
 * @param {boolean} refreshComments - whether to refresh comments list after creation
 * @returns http response with created comment
 */
export const createBuildingModelComment = (
	fileId,
	commentData,
	cb = () => {},
	refreshComments = true
) => {
	return (dispatch) => {
		dispatch({
			type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_REQUEST,
		});

		const requestOptions = {
			method: "POST",
			url: `/building-model-comment/${fileId}`,
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getToken()}`,
			},
			data: commentData,
		};

		api(requestOptions)
			.then((response) => {
				dispatch({
					type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_CREATE_SUCCESS,
					data: response.data.result,
				});
				dispatch(
					setBuildingModelCommentSuccessMessage("Comment created successfully!")
				);
				if (refreshComments) {
					dispatch(getBuildingModelComments(fileId));
				}

				cb(response.data.result);
			})
			.catch((err) => {
				dispatch(
					buildingModelCommentActionFailure(err.response?.data?.message)
				);
			});
	};
};

/**
 * REPLY to building model comment
 *
 * SUCCESS:
 * 		1. Triggers buildingModelCommentReplySuccess
 * 		2. setBuildingModelCommentSuccessMessage
 * 		3. Optionally refresh comments list
 *
 * @param {number} commentId - unique building model comment identifier
 * @param {object} replyData - Contains reply text, metadata, etc.
 * @param {number} fileId - unique building discipline file identifier (for refreshing)
 * @param {function} cb - callback function
 * @param {boolean} refreshComments - whether to refresh comments list after reply
 * @returns http response with created reply
 */
export const replyToBuildingModelComment = (
	commentId,
	replyData,
	fileId = null,
	cb = () => {},
	refreshComments = true
) => {
	return (dispatch) => {
		dispatch({
			type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_REQUEST,
		});

		const requestOptions = {
			method: "POST",
			url: `/building-model-comments-response/${commentId}`,
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getToken()}`,
			},
			data: replyData,
		};

		api(requestOptions)
			.then((response) => {
				dispatch({
					type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_REPLY_SUCCESS,
					data: response.data.result,
				});
				dispatch(
					setBuildingModelCommentSuccessMessage("Reply created successfully!")
				);

				if (refreshComments && fileId) {
					dispatch(getBuildingModelComments(fileId));
				}

				cb(response.data.result);
			})
			.catch((err) => {
				dispatch(
					buildingModelCommentActionFailure(err.response?.data?.message)
				);
			});
	};
};

export const deleteBuildingModelComment = (
	commentId,
	fileId = null,
	cb = () => {},
	refreshComments = true
) => {
	return (dispatch) => {
		dispatch({
			type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_REQUEST,
		});

		const requestOptions = {
			method: "DELETE",
			url: `/building-model-comment/${commentId}`,
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getToken()}`,
			},
		};

		api(requestOptions)
			.then((response) => {
				dispatch({
					type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_DELETE_SUCCESS,
					data: response.data.result,
				});
				dispatch(
					setBuildingModelCommentSuccessMessage("Comment deleted successfully!")
				);
				if (refreshComments && fileId) {
					dispatch(getBuildingModelComments(fileId));
				}

				cb(response.data.result);
			})
			.catch((err) => {
				dispatch(
					buildingModelCommentActionFailure(err.response?.data?.message)
				);
			});
	};
};

//---------------------------------END ENDPOINTS----------------------------------------------

//---------------------------------BUILDING MODEL COMMENT STATE FUNCTIONS-------------------------------------

/**
 * Set success message for building model comments
 * @param {string} message - success message
 * @returns action object
 */
export const setBuildingModelCommentSuccessMessage = (message) => {
	return {
		type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_SET_SUCCESS_MESSAGE,
		data: message,
	};
};

/**
 * Clear building model comments state
 * @returns action object
 */
export const clearBuildingModelComments = () => {
	return { type: buildingModelCommentConstants.CLEAR_BUILDING_MODEL_COMMENTS };
};

/**
 * Building model comment action failure handler
 * @param {string} error - error message
 * @returns action object
 */
export const buildingModelCommentActionFailure = (error) => {
	return {
		type: buildingModelCommentConstants.BUILDING_MODEL_COMMENT_FAILURE,
		error,
	};
};
